import {ComplianceTicketFormData} from "./types";
import {produce} from "immer";
import {
    ComplianceTicket,
    ComplianceTicketFragmentFragment,
    Customer,
    VeriflytAccountFragmentFragment
} from "../../../__generated__/graphql/types";
import {getCollapsePanelKey, toFormData} from "./util";
import uuidv4 from "uuid/v4";
import React, {Dispatch} from "react";
import {createFormField, LabelInValue} from "../../../common/form";

export const DefaultState = {
    formState: new Map<string, ComplianceTicketFormData>(),
    ticketsDirty: false,
    collapseActiveKeys: [] as string[]
}

export type State = typeof DefaultState


export type Action = {
    type: 'UPDATE_TICKET',
    payload: {
        data: ComplianceTicketFragmentFragment | ComplianceTicketFormData
    }
} | {
    type: 'INIT_TICKETS',
    payload: ComplianceTicketFragmentFragment[] | ComplianceTicketFormData[]
} | {
    type: 'ADD_BLANK_TICKET',
    payload: {
        makeVisible?: boolean
    }
} | {
    type: 'REMOVE_TICKET',
    payload: {
        internalId: string
    }
} | {
    type: 'COLLAPSE_SET_ACTIVE_KEYS',
    payload: string[]
} | {
    type: 'COLLAPSE_SET_VISIBILITY',
    payload: {
        key: string,
        visible: boolean
    }
}

const TicketsReducer: React.Reducer<State, Action> = (_state, action) => {
    return produce(_state, (draft) => {
        draft.ticketsDirty = true;
        switch (action.type){
            case "INIT_TICKETS":
                draft.ticketsDirty = false;
                draft.formState = new Map(DefaultState.formState);
                action.payload.forEach((ticket: ComplianceTicketFragmentFragment | ComplianceTicketFormData) => {
                    const ticketForm = toFormData(ticket);
                    draft.formState.set(ticketForm.internalId, ticketForm);
                })

                console.log('INIT_TICKETS', action.payload, draft.formState);
                return draft
            case "UPDATE_TICKET": {
                const ticketForm = toFormData(action.payload.data);

                console.log('ticketForm', ticketForm);
                console.log('data', action.payload.data);

                draft.formState.set(ticketForm.internalId, ticketForm);
                return draft
            }
            case "ADD_BLANK_TICKET": {

                const blankTicket: ComplianceTicketFragmentFragment = {
                    ticketTemplateID: null,
                    catalogueName: null,
                    result_passed: false,
                    _id: null,
                    completionDate: null,
                    expirationDate: null
                }

                const internalId = uuidv4();

                const ticketForm = toFormData(blankTicket, internalId);

                let state = Reducer(_state, {
                    type: 'UPDATE_TICKET',
                    payload: {
                        data: ticketForm
                    }
                });

                if (action.payload.makeVisible) {
                    const action: Action = {
                        type: 'COLLAPSE_SET_VISIBILITY',
                        payload: {
                            key: getCollapsePanelKey(ticketForm),
                            visible: true
                        }
                    }
                    state = Reducer(state, action);
                }

                return Object.assign(draft, state);
            }
            case "REMOVE_TICKET":
                draft.formState.delete(action.payload.internalId);
                return draft;
        }
    })
}

export const Reducer: React.Reducer<State, Action> = (_state, action) => {
    return produce(_state, (draft) => {
        switch (action.type){
            case "COLLAPSE_SET_ACTIVE_KEYS":
                draft.collapseActiveKeys = action.payload;
                return draft;
            case "COLLAPSE_SET_VISIBILITY":
                if (action.payload.visible && !_state.collapseActiveKeys.includes(action.payload.key)){
                    draft.collapseActiveKeys.push(action.payload.key);
                }
                else if (!action.payload.visible && _state.collapseActiveKeys.includes(action.payload.key)){
                    draft.collapseActiveKeys = draft.collapseActiveKeys.filter((key) => key !== action.payload.key);
                }

                return draft;
            default:
                return TicketsReducer(_state, action);
        }
    })
}

export interface ComplianceEditorApiSideEffects {
    onResetTickets: () => void
}

export class ComplianceEditorApi {

    private readonly dispatcher: Dispatch<Action>
    private readonly state: State;
    private readonly sideEffects: ComplianceEditorApiSideEffects;

    constructor(state: State, dispatcher: Dispatch<Action>, sideEffects?: ComplianceEditorApiSideEffects) {
        this.state = state;
        this.dispatcher = dispatcher;
        this.sideEffects = sideEffects;
    }

    public addBlankTicket = (options?: { makeVisible?: boolean }) => {
        this.dispatcher({ type: 'ADD_BLANK_TICKET', payload: { makeVisible: options?.makeVisible } });
    }

    public updateTicket = (ticketData: ComplianceTicket | ComplianceTicketFormData) => {
        this.dispatcher({ type: 'UPDATE_TICKET', payload: { data: ticketData } });
    }

    public removeTicket = (ticket: ComplianceTicketFormData | string) => {
        let formId = typeof ticket === 'string' ? ticket : ticket?.internalId;
        this.dispatcher({ type: 'REMOVE_TICKET', payload: { internalId: formId } })
    }

    public resetTickets = () => {
        if (typeof this.sideEffects?.onResetTickets === 'function'){
            this.sideEffects.onResetTickets();
        }
    }

    public setCollapseKeys = (keys: string[]) => {
        this.dispatcher({ type: 'COLLAPSE_SET_ACTIVE_KEYS', payload: keys })
    }

    public setTicketVisibility = (ticket: ComplianceTicketFormData, visible: boolean) => {
        const key = getCollapsePanelKey(ticket);
        this.dispatcher({ type: 'COLLAPSE_SET_VISIBILITY', payload: { key, visible } })
    }

    public toggleTicketVisibility = (ticket: ComplianceTicketFormData) => {
        const key = getCollapsePanelKey(ticket);
        this.setTicketVisibility(ticket, !(key in this.state.collapseActiveKeys))
    }

    public areTicketsDirty = () => {
        return this.state.ticketsDirty;
    }
}